// frontend/src/pages/TermsPage.js
import React from 'react';

export default function TermsPage() {
  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-2">Terms of Use</h1>
      <p className="text-sm text-gray-500 mb-6">Last updated: December 8, 2025</p>

      <p className="text-gray-700 mb-4">
        ScalableDeploy is a technology services brand of OL Trading LLC
        (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). These Terms of Use
        (&quot;Terms&quot;) govern your access to and use of our website
        scalabledeploy.com and our AWS/AI consulting and engineering services
        (the &quot;Services&quot;). By using the site or engaging our Services,
        you agree to these Terms.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">1. Services</h2>
      <p className="text-gray-700 mb-4 text-sm">
        We provide AI and cloud engineering services, including but not limited
        to RAG pilots, production search/chat stacks, image pipelines, cost
        audits, and related AWS infrastructure work. The specific scope,
        deliverables, and pricing for each engagement are defined in writing
        (for example, via email, proposal, or order form).
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">2. Use of the site</h2>
      <p className="text-gray-700 mb-4 text-sm">
        You agree to use the site only for lawful purposes and in accordance
        with these Terms. You must not attempt to interfere with the operation
        of the site, reverse engineer any part of it, or use it to transmit
        malicious code or unauthorized content.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">3. Payments and billing</h2>
      <p className="text-gray-700 mb-2 text-sm">
        Unless otherwise specified in writing:
      </p>
      <ul className="list-disc pl-5 text-gray-700 space-y-1 mb-4 text-sm">
        <li>
          Payments for fixed-scope packages (such as the RAG Starter Kit) are
          typically due at the start of the engagement.
        </li>
        <li>
          Larger or ongoing projects may be billed per milestone or on a
          recurring basis as agreed.
        </li>
        <li>
          Payments are processed securely via third-party payment processors
          such as Stripe. By providing payment information, you authorize us
          and our processor to charge the applicable amounts.
        </li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">4. Refunds and cancellations</h2>
      <p className="text-gray-700 mb-2 text-sm">
        Our goal is to make engagements clear and low-risk. Unless otherwise
        agreed in writing:
      </p>
      <ul className="list-disc pl-5 text-gray-700 space-y-1 mb-4 text-sm">
        <li>
          If no work has been delivered yet, we may issue a refund upon request.
        </li>
        <li>
          Once work has begun, refunds may be prorated based on the portion of
          work completed and any non-refundable third-party costs.
        </li>
        <li>
          You may cancel an engagement at any time; any unused prepaid work
          (if applicable) is refunded or credited toward future work.
        </li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">5. Client content and data</h2>
      <p className="text-gray-700 mb-4 text-sm">
        In order to provide the Services, you may supply us with content,
        datasets, configuration details, or other materials (&quot;Client
        Content&quot;). You retain ownership of your Client Content. You grant
        us a limited license to use Client Content solely for the purpose of
        delivering the Services and improving the specific solution we build
        for you.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">6. No warranties</h2>
      <p className="text-gray-700 mb-4 text-sm">
        While we aim for high-quality, practical solutions, the site and
        Services are provided &quot;as is&quot; and &quot;as available&quot;
        without warranties of any kind, whether express or implied. We do not
        guarantee specific business outcomes, revenue, or performance metrics,
        and you are responsible for how you choose to deploy and rely on the
        outputs of any AI systems we help build.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">7. Limitation of liability</h2>
      <p className="text-gray-700 mb-4 text-sm">
        To the maximum extent permitted by law, we are not liable for any
        indirect, incidental, special, or consequential damages, or any loss of
        profits or data, arising out of or in connection with the use of the
        site or Services. Our total liability for any claim relating to the
        Services is limited to the amounts you paid us for the specific
        engagement giving rise to the claim.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">8. Intellectual property</h2>
      <p className="text-gray-700 mb-4 text-sm">
        We retain ownership of our pre-existing tools, libraries, templates,
        and know-how (&quot;Background IP&quot;). Unless otherwise agreed in
        writing, you receive a non-exclusive license to use the deliverables we
        create for you in your business, subject to payment of the applicable
        fees. Any use of our name, logo, or marketing materials requires our
        prior written permission.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">9. Privacy</h2>
      <p className="text-gray-700 mb-4 text-sm">
        Our handling of personal information is described in our{' '}
        <a href="/privacy" className="underline">
          Privacy Policy
        </a>
        . By using the site or Services, you acknowledge that we may process
        information in accordance with that policy.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">10. Changes to these Terms</h2>
      <p className="text-gray-700 mb-4 text-sm">
        We may update these Terms from time to time. When we do, we will revise
        the &quot;Last updated&quot; date at the top of this page. Your
        continued use of the site or Services after any changes become
        effective constitutes your acceptance of the updated Terms.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">11. Contact</h2>
      <p className="text-gray-700 mb-2 text-sm">
        If you have any questions about these Terms or an engagement, you can
        contact us at:
      </p>
      <p className="text-gray-700 text-sm">
        Email:{' '}
        <a href="mailto:hello@scalabledeploy.com" className="underline">
          hello@scalabledeploy.com
        </a>
      </p>
    </div>
  );
}
